/**
 * Utilities for managing recently visited breakdowns
 */
import { getCookie, setCookie } from './cookieUtils';
import { getUserId } from './tokenUtils';

// Base cookie name for storing recent breakdowns
const RECENT_BREAKDOWNS_BASE = 'recentBreakdowns';
// Maximum number of recent breakdowns to store
const MAX_RECENT_BREAKDOWNS = 5;

/**
 * Get the user-specific cookie name for recent breakdowns
 * @returns {string} Cookie name with user ID
 */
const getRecentBreakdownsCookieName = () => {
  const userId = getUserId();
  return userId ? `${RECENT_BREAKDOWNS_BASE}_${userId}` : RECENT_BREAKDOWNS_BASE;
};

/**
 * Get recent breakdowns from cookie
 * @returns {Array} Array of recently visited breakdown objects
 */
export const getRecentBreakdowns = () => {
  // Get the user-specific cookie name
  const cookieName = getRecentBreakdownsCookieName();
  const recentBreakdownsJson = getCookie(cookieName);
  
  if (!recentBreakdownsJson) {
    return [];
  }
  
  try {
    return JSON.parse(decodeURIComponent(recentBreakdownsJson));
  } catch (error) {
    console.error('Error parsing recent breakdowns', error);
    return [];
  }
};

/**
 * Add a breakdown to recent breakdowns
 * @param {Object} breakdown - The breakdown to add
 */
export const addRecentBreakdown = (breakdown) => {
  if (!breakdown || !breakdown.id) return;
  
  // Get the user ID - don't store breakdowns if no user ID is available
  const userId = getUserId();
  if (!userId) {
    console.warn('No user ID available, skipping recent breakdown tracking');
    return;
  }
  
  // Create a simplified version of the breakdown with only necessary fields
  const simplifiedBreakdown = {
    id: breakdown.id,
    breakdownReference: breakdown.breakdownReference,
    breakdownDescription: breakdown.description,
    breakdownStatus: breakdown.descriptionName,
    dateTimeOffset: breakdown.dateTimeOffset,
    visitedAt: new Date().toISOString()
  };

  // Get current recent breakdowns
  let recentBreakdowns = getRecentBreakdowns();
  
  // Remove this breakdown if it already exists (to avoid duplicates)
  recentBreakdowns = recentBreakdowns.filter(b => b.id !== breakdown.id);
  
  // Add the new breakdown to the front of the array
  recentBreakdowns.unshift(simplifiedBreakdown);
  
  // Limit the number of stored breakdowns
  if (recentBreakdowns.length > MAX_RECENT_BREAKDOWNS) {
    recentBreakdowns = recentBreakdowns.slice(0, MAX_RECENT_BREAKDOWNS);
  }
  
  // Get the user-specific cookie name
  const cookieName = getRecentBreakdownsCookieName();
  
  // Save to cookie (7 days expiration)
  setCookie(
    cookieName, 
    encodeURIComponent(JSON.stringify(recentBreakdowns)), 
    7
  );
};

/**
 * Remove a breakdown from recent breakdowns
 * @param {number|string} breakdownId - The ID of the breakdown to remove
 * @returns {boolean} True if the breakdown was found and removed, false otherwise
 */
export const removeRecentBreakdown = (breakdownId) => {
  if (!breakdownId) return false;
  
  // Convert breakdownId to string to ensure consistent comparison
  const idToRemove = String(breakdownId);
  
  // Get the user ID
  const userId = getUserId();
  if (!userId) {
    console.warn('No user ID available, skipping recent breakdown removal');
    return false;
  }
  
  // Get the user-specific cookie name first
  const cookieName = getRecentBreakdownsCookieName();
  
  // Get current recent breakdowns
  let recentBreakdowns = getRecentBreakdowns();
  
  // Check if the breakdown exists in the list
  const initialLength = recentBreakdowns.length;
  
  // Filter out the breakdown with the matching ID - convert both to strings for comparison
  recentBreakdowns = recentBreakdowns.filter(b => String(b.id) !== idToRemove);
  
  // If the length didn't change, the breakdown wasn't in the list
  if (recentBreakdowns.length === initialLength) {
    return false;
  }
  
  // Save the updated list to cookie (7 days expiration)
  const cookieValue = encodeURIComponent(JSON.stringify(recentBreakdowns));
  
  // Try to set the cookie with path and secure flags
  setCookie(
    cookieName, 
    cookieValue, 
    7
  );
  
  return true;
};
